import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { Upload } from 'lucide-react';

const GLBAnimationViewer = () => {
  const containerRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const sceneRef = useRef(null);
  const mixerRef = useRef(null);
  const clockRef = useRef(new THREE.Clock());
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      75,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 2, 5);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    rendererRef.current = renderer;
    containerRef.current.appendChild(renderer.domElement);

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 10, 5);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    const pointLight = new THREE.PointLight(0xffffff, 0.5);
    pointLight.position.set(-5, 5, -5);
    scene.add(pointLight);

    // Grid helper
    const gridHelper = new THREE.GridHelper(10, 10, 0x444444, 0x222222);
    scene.add(gridHelper);

    // Animation loop
    let animationId;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      
      if (mixerRef.current) {
        const delta = clockRef.current.getDelta();
        mixerRef.current.update(delta);
      }

      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current) return;
      camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.glb')) {
      setError('Please upload a .glb file');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const blob = new Blob([arrayBuffer]);
      const url = URL.createObjectURL(blob);

      const loader = new GLTFLoader();
      
      loader.load(
        url,
        (gltf) => {
          // Clear previous model
          if (sceneRef.current) {
            const objectsToRemove = [];
            sceneRef.current.traverse((child) => {
              if (child.isMesh || (child.isGroup && child.parent === sceneRef.current)) {
                objectsToRemove.push(child);
              }
            });
            objectsToRemove.forEach(obj => {
              if (obj.parent) {
                obj.parent.remove(obj);
              }
            });
          }

          // Add new model
          const model = gltf.scene;
          sceneRef.current.add(model);

          // Center model
          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          
          model.position.sub(center);
          model.position.y += size.y / 2;

          // Adjust camera based on model size
          const maxDim = Math.max(size.x, size.y, size.z);
          const fov = cameraRef.current.fov * (Math.PI / 180);
          let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
          cameraZ *= 2.5;
          cameraRef.current.position.set(0, maxDim * 0.5, cameraZ);
          cameraRef.current.lookAt(0, 0, 0);

          // Setup animation
          if (gltf.animations && gltf.animations.length > 0) {
            mixerRef.current = new THREE.AnimationMixer(model);
            
            // Play first animation by default
            const action = mixerRef.current.clipAction(gltf.animations[0]);
            action.play();
            
            
            setError(`✓ Model loaded with ${gltf.animations.length} animation(s)`);
          } else {
            
            setError('✓ Model loaded (no animations found)');
          }

          URL.revokeObjectURL(url);
          setLoading(false);
        },
        (progress) => {
          const percent = Math.round((progress.loaded / progress.total) * 100);
          
        },
        (error) => {
          console.error('Error loading GLB:', error);
          setError('Error loading GLB file. Please try another file.');
          setLoading(false);
          URL.revokeObjectURL(url);
        }
      );
    } catch (err) {
      console.error('Error:', err);
      setError('Error processing file. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-screen flex flex-col bg-gray-900">
      <div className="bg-gray-800 p-4 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <h1 className="text-2xl font-bold text-white">GLB Animation Viewer</h1>
          
          <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors">
            <Upload size={20} />
            <span>Upload GLB</span>
            <input
              type="file"
              accept=".glb"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>

          {loading && (
            <span className="text-gray-300">Loading...</span>
          )}
        </div>

        {error && (
          <div className={`max-w-4xl mx-auto mt-2 p-3 rounded ${
            error.startsWith('✓') 
              ? 'bg-green-900 text-green-200' 
              : 'bg-red-900 text-red-200'
          }`}>
            {error}
          </div>
        )}
      </div>

      <div ref={containerRef} className="flex-1" />

      <div className="bg-gray-800 p-3 text-center text-sm text-gray-400">
        Upload a .glb file to view and play its animations automatically
      </div>
    </div>
  );
};

export default GLBAnimationViewer;